import { useEffect, useRef, useState } from 'react';
import { Typography } from 'antd';

import ItemLookupSelect from '../common/ItemLookupSelect';
import { DataState, Skeleton, deriveDataState } from '../loading';
import { previewDrivenItem } from '../../services/cardDefs';
import { apiErrorMessage } from '../../utils/apiErrors';
import { formatCalculatedValue } from '../../utils/formulaTokens';
import { itemsDoorPath } from '../../utils/listLinks';
import { normalizeId } from '../../utils/ids';
import { EXTERNAL_LINK_PROPS } from '../common/ExternalLink';

const { Text } = Typography;

/**
 * "Try it on" (KPR-959, KPE-010): the driven value dialog's answer to "is this
 * rule right?" before the card is saved.
 *
 * The SERVER works the unsaved rule out for one item of the card
 * (`POST /api/card-definitions/{id}/driven-item-preview`): the value the save
 * would store, how many source items it read, how many of those this person
 * can open, and a door — a list query the server built and checked — that
 * opens exactly those. Nothing here computes a value or builds a query; the
 * dialog only carries them (knowledge: doors-to-a-filtered-list).
 *
 * The item is the one picked, else the server's: the most recently saved item
 * of the card the person can read. Every change to the rule asks again, 500 ms
 * after the last one (a pick of another item asks at once), and an answer to
 * a question no longer asked is dropped. The count opens its list in a new
 * tab, so the unsaved rule stays in the dialog.
 *
 * `ready` is false while the rule cannot be asked about yet (incomplete, or a
 * condition with a problem); `notReady` says why in words.
 */

const DEBOUNCE_MS = 500;

// What a person reads for each aggregate: "5 items counted".
const READ_VERBS = { count: 'counted', sum: 'summed', average: 'averaged', min: 'compared', max: 'compared', latest: 'compared' };

const plural = (n, one, many) => `${Number(n).toLocaleString()} ${n === 1 ? one : many}`;

// "elements.x.options.drivenFrom.where.filter: " is the API's field path, not words for a person.
const withoutFieldPath = text => String(text || '').replace(/^elements\.[^:]*:\s*/, '');

function errorText(error, itemId) {
	const status = error?.response?.status;
	if (status === 429) return apiErrorMessage(error);
	if (status === 404) return itemId ? 'That item can’t be opened any more. Pick another.' : 'Trying a rule on an item isn’t available here yet.';
	if (status === 400) return withoutFieldPath(apiErrorMessage(error));
	return apiErrorMessage(error, 'Something went wrong. Try again.');
}

function SourcesLine({ answer, aggregate, entries }) {
	const sources = answer?.sources;
	// A rule that reads an element hidden from this person: no count, no list (said with the value).
	if (!sources || sources.count === null || sources.count === undefined) return null;
	const verb = READ_VERBS[aggregate] || 'read';
	// Across a group's entries (KPR-1055) the sources are the items that HOLD the
	// entries the value reads, so the line says that, not "items counted".
	const label = entries
		? `${plural(sources.count, 'item', 'items')} holding the entries ${verb}`
		: `${plural(sources.count, 'item', 'items')} ${verb}`;
	const href = itemsDoorPath(answer.collectionId, sources.door);
	const hidden = Number.isFinite(sources.visible) ? sources.count - sources.visible : 0;
	return (
		<p className='driven-try__sources'>
			{href ? (
				<Typography.Link href={href} {...EXTERNAL_LINK_PROPS}>{label}</Typography.Link>
			) : <span>{label}</span>}
			{hidden > 0 ? (
				<Text type='secondary'>{` · ${plural(hidden, 'item', 'items')} you can’t open ${hidden === 1 ? 'is' : 'are'} left out of the list.`}</Text>
			) : null}
			{sources.doorReason === 'hidden' ? (
				<Text type='secondary'> · They can’t be listed for you: the rule picks them by an element you can’t see.</Text>
			) : null}
			{sources.doorReason === 'too_many' ? (
				<Text type='secondary'> · Too many to open as one list here.</Text>
			) : null}
		</p>
	);
}

export default function DrivenTryIt({ cardId, collectionId, element, aggregate, entries = false, ready, notReady }) {
	const [itemId, setItemId] = useState('');
	const [answer, setAnswer] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [attempt, setAttempt] = useState(0);
	const generation = useRef(0);
	const lastItem = useRef(itemId);

	const card = normalizeId(cardId);
	const askable = !!(card && ready && element?.name);
	// One string per question: the rule as it would be saved, and the item.
	const question = askable ? JSON.stringify({ element, itemId, attempt }) : null;

	useEffect(() => {
		if (!question) return undefined;
		const mine = ++generation.current;
		const asked = JSON.parse(question);
		const delay = lastItem.current !== asked.itemId ? 0 : DEBOUNCE_MS;
		lastItem.current = asked.itemId;
		setLoading(true);
		const timer = setTimeout(async () => {
			try {
				const data = await previewDrivenItem(card, { elementName: asked.element.name, elements: [asked.element], itemId: asked.itemId });
				if (mine !== generation.current) return;
				setAnswer(data);
				setError(null);
			} catch (e) {
				if (mine !== generation.current) return;
				setError(errorText(e, asked.itemId));
			} finally {
				if (mine === generation.current) setLoading(false);
			}
		}, delay);
		return () => clearTimeout(timer);
	}, [question, card]);

	// A question no longer asked (the rule became incomplete) leaves nothing waiting.
	useEffect(() => {
		if (!question) { generation.current++; setLoading(false); }
	}, [question]);

	const shownItemId = itemId || normalizeId(answer?.item?._id) || '';
	const hasData = !!answer;
	const isEmpty = hasData && !answer.item;

	let body = null;
	if (!card) {
		body = <Text type='secondary'>Save the card first, then try the rule on one of its items.</Text>;
	} else if (!askable) {
		body = <Text type='secondary'>{notReady || 'Finish the rule to try it on an item.'}</Text>;
	} else {
		body = (
			<DataState
				state={deriveDataState({ loading, error, hasData, isEmpty })}
				hasData={hasData}
				isEmpty={isEmpty}
				error={error}
				onRetry={() => setAttempt(n => n + 1)}
				label='the preview'
				skeleton={<Skeleton variant='line' count={2} />}
				empty={<Text type='secondary'>No item of this card to try it on yet. Add one, then come back.</Text>}
				className='driven-try__state'
			>
				{answer?.item ? (
					<div className='driven-try__answer' aria-live='polite'>
						{answer.sources?.doorReason === 'hidden' && answer.value === null ? (
							<p className='driven-try__value'>This rule reads an element you can’t see, so it can’t be tried for you here.</p>
						) : answer.overridden ? (
							<p className='driven-try__value'>This item’s card works the element out its own way, so this rule doesn’t apply to it.</p>
						) : answer.incomplete && answer.value === null ? (
							<p className='driven-try__value'>This took too long to work out here. Saving the rule still works.</p>
						) : (
							<p className='driven-try__value'>
								On this item, the rule gives <strong>{formatCalculatedValue(element, answer.value)}</strong>.
								{answer.refused ? (
									<Text type='secondary'>{` ${element?.label?.singular || 'This element'} can’t hold what the rule gives, so it would be left blank.`}</Text>
								) : null}
							</p>
						)}
						<SourcesLine answer={answer} aggregate={aggregate} entries={entries} />
						{answer.incomplete && answer.value !== null ? (
							<Text type='secondary' className='d-block'>Counting the items it read took too long here.</Text>
						) : null}
					</div>
				) : null}
			</DataState>
		);
	}

	return (
		<div className='driven-try'>
			<div className='driven-try__head'>
				<Text strong id='drivenTryItLabel'>Try it on</Text>
				{askable && shownItemId ? (
					<ItemLookupSelect
						collectionId={collectionId}
						cardId={card}
						value={shownItemId}
						allowClear={false}
						onChange={(next) => { if (next) setItemId(normalizeId(next)); }}
						ariaLabel='Try it on'
						placeholder='Pick an item'
						className='driven-try__select'
					/>
				) : null}
			</div>
			{body}
		</div>
	);
}
